import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { FacilityResponse } from "@bw/shared/types/generated";

import {
  formatLateJoinTiming,
  formatQueueMode,
  formatQueueStrategy,
} from "./facilityDetailFormatters";

function PolicyFact({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function FacilityQueuePolicyPanel({
  facility,
}: {
  facility: FacilityResponse;
}) {
  const auto = facility.queue_assignment_mode === "auto";
  const autoValue = (value: string) => (auto ? value : "Inactive");

  return (
    <Paper
      component="section"
      aria-labelledby="queue-policy-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}
    >
      <Stack spacing={2}>
        <Typography
          id="queue-policy-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700 }}
        >
          Drop-in queue policy
        </Typography>
        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            m: 0,
          }}
        >
          <PolicyFact
            label="Queue state"
            value={facility.queue_open ? "Open" : "Closed"}
          />
          <PolicyFact
            label="Assignment mode"
            value={formatQueueMode(facility.queue_assignment_mode)}
          />
          <PolicyFact
            label="Court block"
            value={`${facility.queue_session_duration_minutes} minutes`}
          />
          <PolicyFact
            label="Active-court minimum"
            value={`${facility.queue_minimum_remaining_minutes} minutes`}
          />
          <PolicyFact
            label="Auto strategy"
            value={autoValue(
              formatQueueStrategy(facility.queue_auto_court_strategy),
            )}
          />
          <PolicyFact
            label="Target occupancy"
            value={autoValue(`${facility.queue_auto_target_occupancy} players`)}
          />
          <PolicyFact
            label="Minimum starting occupancy"
            value={autoValue(
              `${facility.queue_auto_minimum_starting_occupancy} players`,
            )}
          />
          <PolicyFact
            label="Late-join court time"
            value={autoValue(
              formatLateJoinTiming(facility.queue_auto_late_join_timing_policy),
            )}
          />
          <PolicyFact
            label="Maximum total wait"
            value={autoValue(`${facility.queue_auto_max_wait_minutes} minutes`)}
          />
        </Box>
      </Stack>
    </Paper>
  );
}
